import React, { useEffect, useState } from "react";
import { Calendar, FileText, AlertCircle, Users } from "lucide-react";
import { api, fmtDate } from "@/lib/api";

const CAT_LABELS = { news: "Actualité", event: "Événement", exam: "Examen", meeting: "Réunion", communique: "Communiqué" };
const CAT_ICONS = { news: FileText, event: Calendar, exam: AlertCircle, meeting: Users, communique: FileText };
const CAT_COLORS = { news: "text-[#166534] bg-[#166534]/10", event: "text-[#EA580C] bg-[#EA580C]/10", exam: "text-red-600 bg-red-50", meeting: "text-[#1E3A8A] bg-[#1E3A8A]/10", communique: "text-[#FBBF24] bg-[#FBBF24]/10" };

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/announcements?limit=50").then(({ data }) => {
      setItems(data);
      // Fire-and-forget view increments
      data.forEach(a => {
        if (a.id) api.post(`/announcements/${a.id}/view`).catch(() => {});
      });
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#F8FAFC] min-h-screen py-16" data-testid="page-news">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="chip mb-4"><span>Actualités & événements</span></div>
        <h1 className="font-heading text-5xl font-extrabold text-[#1E3A8A] tracking-tight mb-3">
          La vie de l&apos;école <span className="text-[#EA580C]">au jour le jour</span>.
        </h1>
        <p className="text-slate-600 max-w-2xl mb-12">Tous les communiqués, événements et informations importantes des Écoles Mazid.</p>

        {loading ? (
          <div className="text-center text-slate-500 py-12">Chargement...</div>
        ) : (
          <div className="space-y-4">
            {items.map((n, i) => {
              const Icon = CAT_ICONS[n.category] || FileText;
              const colorClass = CAT_COLORS[n.category] || CAT_COLORS.news;
              return (
                <article key={i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#166534] transition-colors" data-testid={`news-item-${i}`}>
                  {n.image_url && (
                    <div className="aspect-[21/9] overflow-hidden bg-slate-100">
                      <img src={resolveImg(n.image_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-start gap-5">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="h-7 w-7" strokeWidth={1.6} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="chip">{CAT_LABELS[n.category] || "Actualité"}</span>
                          <span className="text-xs text-slate-500">{fmtDate(n.created_at)}</span>
                        </div>
                        <h2 className="font-heading font-bold text-2xl text-[#1E3A8A] mb-3 leading-snug">{n.title}</h2>
                        <p className="text-slate-700 leading-relaxed">{n.content}</p>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
            {items.length === 0 && (
              <div className="text-center text-slate-500 py-12 bg-white rounded-2xl border border-slate-200">
                Aucune actualité pour le moment.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
